import {useCallback, useState, useEffect, useMemo} from 'react';
import {useGTMDispatch} from '@elgorditosalsero/react-gtm-hook';
import isSafari from 'shared/utils/isSafari';
import {AddToHomePromptContextValue} from './AddToHomePromptContext';

interface IBeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const isInWebApp =
  window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

export default function useAddToHomeScreenPrompt(): AddToHomePromptContextValue & {
  showSafariModal: boolean;
  closeIosModal: () => void;
} {
  const [prompt, setPrompt] = useState<IBeforeInstallPromptEvent | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [showSafariModal, setShowSafariModal] = useState(false);
  const [hasFinishedInstallation, setHasFinishedInstallation] = useState(isInWebApp);

  const sendDataToGTM = useGTMDispatch();
  const closeIosModal = useCallback(() => setShowSafariModal(false), []);

  const finishInstallation = useCallback(function() {
    setHasFinishedInstallation(true);
  }, []);

  const promptToInstall = useCallback(
    function() {
      if (isSafari()) {
        setShowSafariModal(true);
        return;
      }
      if (prompt) {
        sendDataToGTM({
          event: 'instalar_app',
          action: 'open'
        });
        prompt.prompt();
        prompt.userChoice.then(function(choiceResult) {
          if (choiceResult.outcome === 'accepted') {
            finishInstallation();
          } else {
            sendDataToGTM({
              event: 'finish',
              action: 'finish'
            });
          }
        });
        return;
      }

      return Promise.reject(
        new Error('Tried installing before browser sent "beforeinstallprompt" event')
      );
    },
    [finishInstallation, prompt, sendDataToGTM]
  );

  useEffect(
    function() {
      if (prompt || isSafari()) {
        setIsReady(true);
      }
    },
    [prompt]
  );

  useEffect(function() {
    function ready(e: IBeforeInstallPromptEvent) {
      e.preventDefault();
      setPrompt(e);
    }

    window.addEventListener('beforeinstallprompt', ready as any);

    return function() {
      window.removeEventListener('beforeinstallprompt', ready as any);
    };
  }, []);

  return useMemo(
    () => ({
      isReady,
      promptToInstall,
      hasFinishedInstallation,
      finishInstallation,
      showSafariModal,
      closeIosModal
    }),
    [
      isReady,
      promptToInstall,
      hasFinishedInstallation,
      finishInstallation,
      showSafariModal,
      closeIosModal
    ]
  );
}
